import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Trash2, CheckCircle2, AlertTriangle, WifiOff } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { getPending, updatePending, removePending } from "../lib/offlineQueue";

const ST_LABEL = {
  pending: "Pendiente de envío", sending: "Enviando…",
  duplicate_review: "Requiere revisión por duplicado", sent: "Enviado", error: "Error de envío",
};

export default function PendingReports() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [dup, setDup] = useState(null); // {item, matches}

  const load = () => getPending().then(setItems);
  useEffect(() => {
    load();
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const buildPayload = (item, dupInfo) => {
    const f = item.form;
    return {
      case_type: item.case_type,
      person: { ...f.person, approximate_age: f.person.approximate_age ? Number(f.person.approximate_age) : null },
      location: f.location, reporter: f.reporter,
      primary_photo_id: f.primary_photo_id, approximate_date: f.approximate_date,
      good_faith_confirmation: f.good_faith_confirmation, terms_accepted: f.terms_accepted, privacy_accepted: f.privacy_accepted,
      photo_confirmations: { is_graphic: false, contains_document: false, is_deceased: false },
      possible_duplicate_of: dupInfo?.id || null,
      duplicate_match_reason: dupInfo?.match_reason || null,
      duplicate_match_level: dupInfo?.match_level || null,
    };
  };

  const create = async (item, dupInfo) => {
    await updatePending({ ...item, status: "sending" }); load();
    try {
      const { data } = await api.post("/cases", buildPayload(item, dupInfo));
      await removePending(item.id); load();
      toast.success(`Reporte enviado. Código: ${data.public_case_code}`);
      setDup(null);
      navigate(`/caso/${data.public_case_code}`);
    } catch (e) {
      await updatePending({ ...item, status: "error", error: formatApiError(e.response?.data?.detail) }); load();
      toast.error("No se pudo enviar. Puedes reintentar.");
      setDup(null);
    }
  };

  const send = async (item) => {
    if (!navigator.onLine) { toast.error("No hay conexión. Inténtalo cuando vuelvas a estar en línea."); return; }
    await updatePending({ ...item, status: "sending" }); load();
    const f = item.form;
    try {
      const { data } = await api.post("/cases/check-duplicates", {
        case_type: item.case_type,
        person: { ...f.person, approximate_age: f.person.approximate_age ? Number(f.person.approximate_age) : null },
        location: f.location, approximate_date: f.approximate_date,
      });
      if (data.matches && data.matches.length) {
        await updatePending({ ...item, status: "duplicate_review" }); load();
        setDup({ item, matches: data.matches });
        return;
      }
    } catch (e) { /* continue to create if check fails */ }
    await create(item, null);
  };

  const del = async (id) => { await removePending(id); load(); toast.success("Reporte local eliminado."); };

  return (
    <div className="max-w-lg mx-auto space-y-5">
      <h1 className="font-heading text-2xl font-bold text-[#0F172A]">Reportes pendientes</h1>
      {!online && (
        <div className="flex items-center gap-2 text-sm text-[#991B1B] bg-[#FEE2E2] rounded-lg p-3" data-testid="pending-offline-note">
          <WifiOff className="w-4 h-4" /> Sin conexión. Podrás enviar cuando vuelvas a estar en línea.
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-[#94A3B8] py-10 text-center" data-testid="pending-empty">No tienes reportes pendientes en este dispositivo.</p>
      ) : items.map((it) => (
        <div key={it.id} className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-2" data-testid={`pending-item-${it.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#EFF6FF] text-[#1E40AF]">
              {it.case_type === "missing" ? "Desaparecida" : "Encontrada con vida"}
            </span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-[#F1F5F9] text-[#475569]" data-testid={`pending-status-${it.id}`}>{ST_LABEL[it.status] || it.status}</span>
          </div>
          <p className="text-sm font-medium text-[#0F172A]">
            {[it.form?.person?.first_name, it.form?.person?.last_name].filter(Boolean).join(" ") || "Sin nombre"}
          </p>
          <p className="text-xs text-[#94A3B8]">Guardado: {it.created_at ? new Date(it.created_at).toLocaleString() : "—"}</p>
          {it.error && <p className="text-xs text-[#991B1B]">{it.error}</p>}
          <div className="flex gap-2 pt-1">
            <button onClick={() => send(it)} disabled={!online || it.status === "sending"} data-testid={`pending-send-${it.id}`}
              className="inline-flex items-center gap-1 h-10 px-4 rounded-lg bg-[#003893] text-white text-sm font-semibold disabled:opacity-50">
              <UploadCloud className="w-4 h-4" /> {it.status === "error" ? "Reintentar" : "Enviar"}
            </button>
            <button onClick={() => del(it.id)} data-testid={`pending-delete-${it.id}`}
              className="inline-flex items-center gap-1 h-10 px-3 rounded-lg bg-[#F1F5F9] text-[#475569] text-sm font-medium">
              <Trash2 className="w-4 h-4" /> Eliminar
            </button>
          </div>
        </div>
      ))}

      {dup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 overflow-y-auto" data-testid="pending-duplicate-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setDup(null); load(); }} />
          <div className="relative bg-white rounded-2xl p-5 max-w-lg w-full my-6 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-6 h-6 text-[#D97706] shrink-0" />
              <div>
                <h3 className="font-heading text-lg font-bold">Posibles coincidencias</h3>
                <p className="text-sm text-[#475569]">Encontramos reportes que podrían ser la misma persona. Revisa antes de enviar.</p>
              </div>
            </div>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {dup.matches.map((m) => (
                <div key={m.id} className="border border-[#E2E8F0] rounded-lg p-2.5" data-testid={`pending-dup-${m.public_case_code}`}>
                  <p className="text-sm font-medium">{m.first_name || m.nickname || "Sin nombre"} · <span className="font-mono text-xs">{m.public_case_code}</span></p>
                  <p className="text-xs text-[#94A3B8]">{m.match_reason}</p>
                  <button onClick={() => navigate(`/caso/${m.public_case_code}`)} className="text-xs text-[#1E3A8A] underline">Ver ficha</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setDup(null); load(); }} data-testid="pending-dup-cancel"
                className="flex-1 h-11 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold">Cancelar</button>
              <button onClick={() => create(dup.item, dup.matches[0])} data-testid="pending-dup-continue"
                className="flex-1 h-11 rounded-xl bg-[#003893] text-white font-semibold">Enviar como nuevo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
